import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, FilePlus2, Loader2, MessageSquarePlus, RefreshCcw, RotateCcw, Tag, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";
import { sumsubHub } from "@/lib/api";

export const KINDS = {
  approve: { title: "Approve applicant", icon: CheckCircle2, verb: "Approve", tone: "bg-emerald-600 hover:bg-emerald-700",
    blurb: "Sets the review to completed · GREEN without changing any step — a force approve. Use when a document was wrongly rejected by the automatic checks.", note: "Internal note (optional)" },
  reject: { title: "Reject applicant", icon: XCircle, verb: "Reject", tone: "bg-red-600 hover:bg-red-700", noteRequired: true,
    blurb: "Sets the review to completed · RED · FINAL. Sumsub records it as an external decision — the applicant cannot retry on this level.", note: "Why? (required — stays internal)" },
  reset: { title: "Reset everything", icon: RotateCcw, verb: "Reset everything", tone: "bg-amber-600 hover:bg-amber-700",
    blurb: "Deactivates every uploaded document and clears all step statuses — the applicant does the whole check again on the same link. Not allowed while a check is pending.", note: null },
  request_recheck: { title: "Request a re-check", icon: RefreshCcw, verb: "Send to pending", tone: "bg-sky-600 hover:bg-sky-700",
    blurb: "Moves the applicant to pending so Sumsub reviews the current documents again (not allowed while already pending / queued).", note: "Reason (optional)" },
  request_docs: { title: "Request missing documents", icon: FilePlus2, verb: "Request", tone: "bg-sky-600 hover:bg-sky-700",
    blurb: "Re-opens the journey for an on-hold applicant so they can add what is missing; completed steps are kept.", note: "Message for the record (optional)" },
  tags: { title: "Tags", icon: Tag, verb: "Save tags", tone: "bg-slate-800 hover:bg-slate-900", blurb: "Comma-separated. Existing tags can be ticked off to remove them.", note: null, tags: true },
  note: { title: "Add a note", icon: MessageSquarePlus, verb: "Add note", tone: "bg-slate-800 hover:bg-slate-900", noteRequired: true,
    blurb: "Visible to the team in Sumsub (your name is appended).", note: "Note" },
};

export async function runAction(applicantId, kind, { note = "", step, addTags = "", remove = [] } = {}) {
  if (kind === "reset_step") return sumsubHub.act(applicantId, "reset-step", { step });
  if (kind === "request_recheck") return sumsubHub.act(applicantId, "request-recheck", { reason: note });
  if (kind === "request_docs") return sumsubHub.act(applicantId, "request-docs", { note });
  if (kind === "tags") return sumsubHub.act(applicantId, "tags", { add: addTags.split(",").map((t) => t.trim()).filter(Boolean), remove });
  if (kind === "note") return sumsubHub.act(applicantId, "notes", { note });
  return sumsubHub.act(applicantId, kind, { note });
}

// Full dialog — for the actions that need text (reject, request docs, tags, note).
export function ActionDialog({ kind, applicant, onClose, onDone }) {
  const cfg = KINDS[kind];
  const [note, setNote] = useState("");
  const [addTags, setAddTags] = useState("");
  const [remove, setRemove] = useState(new Set());
  const [busy, setBusy] = useState(false);
  if (!cfg) return null;
  const Icon = cfg.icon;
  const submit = async () => {
    setBusy(true);
    try {
      const res = await runAction(applicant.id, kind, { note, addTags, remove: [...remove] });
      toast.success(`${cfg.title} — done`);
      onDone(res);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message, { duration: 8000 });
    } finally { setBusy(false); }
  };
  const disabled = busy || (cfg.noteRequired && !note.trim()) || (kind === "tags" && !addTags.trim() && !remove.size);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`sumsub-action-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className="w-5 h-5" />{cfg.title}</DialogTitle>
          <DialogDescription>{applicant.first_name} {applicant.last_name} · <span className="font-mono text-xs">{applicant.id}</span></DialogDescription>
        </DialogHeader>
        <p className="rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">{cfg.blurb}</p>
        {cfg.tags && (
          <div className="space-y-2">
            <Input value={addTags} onChange={(e) => setAddTags(e.target.value)} placeholder="new-tag, another-tag" data-testid="sumsub-tags-input" />
            {applicant.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {applicant.tags.map((t) => (
                  <button key={t} type="button" onClick={() => setRemove((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; })} data-testid={`sumsub-tag-toggle-${t}`}>
                    <Badge variant="outline" className={`text-[10px] ${remove.has(t) ? "line-through bg-rose-50 text-rose-700 border-rose-300" : ""}`}>{t}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {cfg.note && <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={cfg.note} data-testid="sumsub-action-note" />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="sumsub-action-cancel">Cancel</Button>
          <Button className={`text-white ${cfg.tone}`} onClick={submit} disabled={disabled} data-testid="sumsub-action-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Icon className="w-4 h-4 mr-1" />}{cfg.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// One-click confirm popover — for approve / re-check / reset everything (nothing to type, optional note).
export function QuickConfirm({ kind, applicantId, canAct, onDone, children }) {
  const cfg = KINDS[kind];
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const Icon = cfg.icon;
  const go = async () => {
    setBusy(true);
    try {
      const res = await runAction(applicantId, kind, { note });
      toast.success(`${cfg.title} — done`);
      setOpen(false);
      onDone(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message, { duration: 8000 });
    } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={!canAct}>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2 p-3" data-testid={`sumsub-quick-${kind}`}>
        <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="w-4 h-4" />{cfg.title}</div>
        <p className="text-[11px] text-slate-600">{cfg.blurb}</p>
        {cfg.note && <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={cfg.note} className="h-8 text-xs" data-testid={`sumsub-quick-note-${kind}`} />}
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)} disabled={busy} data-testid={`sumsub-quick-cancel-${kind}`}>Cancel</Button>
          <Button size="sm" className={`h-7 text-xs text-white ${cfg.tone}`} onClick={go} disabled={busy} data-testid={`sumsub-quick-confirm-${kind}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Icon className="w-3.5 h-3.5 mr-1" />}{cfg.verb}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const BTN = "h-7 text-xs";
export function QuickActions({ applicantId, canAct, onDone, onDialog }) {
  const title = canAct ? undefined : "Underwriting permission needed";
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="sumsub-action-bar">
      <QuickConfirm kind="approve" applicantId={applicantId} canAct={canAct} onDone={onDone}>
        <Button size="sm" variant="outline" className={`${BTN} border-emerald-300 text-emerald-800 hover:bg-emerald-50`} disabled={!canAct} title={title} data-testid="sumsub-act-approve"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>
      </QuickConfirm>
      <Button size="sm" variant="outline" className={`${BTN} border-red-300 text-red-800 hover:bg-red-50`} disabled={!canAct} title={title} onClick={() => onDialog("reject")} data-testid="sumsub-act-reject"><XCircle className="w-3.5 h-3.5 mr-1" />Reject</Button>
      <QuickConfirm kind="request_recheck" applicantId={applicantId} canAct={canAct} onDone={onDone}>
        <Button size="sm" variant="outline" className={BTN} disabled={!canAct} title={title} data-testid="sumsub-act-request_recheck"><RefreshCcw className="w-3.5 h-3.5 mr-1 text-sky-600" />Re-check</Button>
      </QuickConfirm>
      <Button size="sm" variant="outline" className={BTN} disabled={!canAct} title={title} onClick={() => onDialog("add_doc")} data-testid="sumsub-act-add_doc"><Upload className="w-3.5 h-3.5 mr-1 text-sky-600" />Add document</Button>
      <Button size="sm" variant="outline" className={BTN} disabled={!canAct} title={title} onClick={() => onDialog("request_docs")} data-testid="sumsub-act-request_docs"><FilePlus2 className="w-3.5 h-3.5 mr-1 text-sky-600" />Request docs</Button>
      <QuickConfirm kind="reset" applicantId={applicantId} canAct={canAct} onDone={onDone}>
        <Button size="sm" variant="outline" className={`${BTN} border-amber-300 text-amber-900 hover:bg-amber-50`} disabled={!canAct} title={title} data-testid="sumsub-act-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset everything</Button>
      </QuickConfirm>
      <Button size="sm" variant="ghost" className={BTN} disabled={!canAct} title={title} onClick={() => onDialog("tags")} data-testid="sumsub-act-tags"><Tag className="w-3.5 h-3.5 mr-1 text-slate-500" />Tags</Button>
      <Button size="sm" variant="ghost" className={BTN} disabled={!canAct} title={title} onClick={() => onDialog("note")} data-testid="sumsub-act-note"><MessageSquarePlus className="w-3.5 h-3.5 mr-1 text-slate-500" />Note</Button>
    </div>
  );
}
